/* Apex hero slider. ES5, no build step, no theme dependencies.
   Crossfades full-bleed slides, keeps only the active slide's background
   video playing, and runs an optional autoplay timer that yields to a press,
   focus, pointer-down, hidden tabs and prefers-reduced-motion. */
(function () {
  'use strict';

  /* Matches the opacity transition in sections/hero-custom.liquid. */
  var TRANSITION_MS = 600;

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function noop() {}

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* display:none elements (the off-breakpoint media item) report no rects. */
  function isDisplayed(el) {
    return el.getClientRects().length > 0;
  }

  function HeroSlider(root) {
    this.root = root;
    this.slides = toArray(root.querySelectorAll('[data-apex-hero-slide]'));
    this.prevBtn = root.querySelector('[data-apex-hero-prev]');
    this.nextBtn = root.querySelector('[data-apex-hero-next]');
    this.dots = toArray(root.querySelectorAll('[data-apex-hero-dot]'));
    this.index = 0;
    this.timer = null;
    this.leaveTimer = null;
    this.mediaTimers = [];
    this.hovered = false;
    this.focused = false;
    this.pressed = false;
    this.editing = false;

    this.autoplay = root.getAttribute('data-autoplay') === 'true' && this.slides.length > 1 && !reducedMotion();
    this.delay = Math.max(1000, parseInt(root.getAttribute('data-delay'), 10) || 5000);

    if (this.slides.length === 0) return;

    for (var i = 0; i < this.slides.length; i++) {
      if (this.slides[i].classList.contains('is-active')) this.index = i;
    }

    this.bind();
    this.root.classList.toggle('apex-hero--dots-progress', this.autoplay);
    this.render(null);
    this.play();
  }

  /* ---------------- Dots ---------------- */

  HeroSlider.prototype.renderDots = function () {
    for (var i = 0; i < this.dots.length; i++) {
      var dot = this.dots[i];
      if (i === this.index) {
        dot.classList.add('is-active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('is-active', 'is-running', 'is-paused');
        dot.removeAttribute('aria-current');
      }
    }
  };

  /* Restart the active dot's fill so it always tracks a fresh timer. */
  HeroSlider.prototype.runDot = function () {
    var dot = this.dots[this.index];
    if (!dot) return;
    dot.classList.remove('is-running', 'is-paused');
    /* Force a reflow so removing and re-adding the class restarts the animation. */
    void dot.offsetWidth;
    dot.classList.add('is-running');
  };

  HeroSlider.prototype.pauseDot = function () {
    var dot = this.dots[this.index];
    if (dot && dot.classList.contains('is-running')) dot.classList.add('is-paused');
  };

  /* ---------------- Rendering ---------------- */

  HeroSlider.prototype.render = function (previous) {
    var self = this;
    this.renderDots();

    for (var i = 0; i < this.slides.length; i++) {
      var slide = this.slides[i];
      var active = i === this.index;

      if (active) {
        slide.classList.add('is-active');
        slide.setAttribute('aria-hidden', 'false');
        this.activateMedia(slide);
      } else {
        slide.classList.remove('is-active');
        slide.setAttribute('aria-hidden', 'true');
        this.deactivateMedia(slide, i === previous);
      }
    }

    if (previous !== null && previous !== this.index && this.slides[previous]) {
      var leaving = this.slides[previous];
      leaving.classList.add('is-leaving');
      if (this.leaveTimer) window.clearTimeout(this.leaveTimer);
      this.leaveTimer = window.setTimeout(function () {
        for (var j = 0; j < self.slides.length; j++) {
          self.slides[j].classList.remove('is-leaving');
        }
      }, TRANSITION_MS + 50);
    }
  };

  HeroSlider.prototype.goTo = function (index) {
    var count = this.slides.length;
    if (count < 2) return;
    var next = ((index % count) + count) % count;
    if (next === this.index) return;
    var previous = this.index;
    this.index = next;
    this.render(previous);
    this.play();
  };

  /* ---------------- Media ---------------- */

  HeroSlider.prototype.activateMedia = function (slide) {
    var videos = toArray(slide.querySelectorAll('[data-apex-hero-video]'));
    for (var i = 0; i < videos.length; i++) {
      var video = videos[i];
      if (!isDisplayed(video)) {
        /* Off-breakpoint copy (mobile vs desktop): keep it idle. */
        if (!video.paused) video.pause();
        continue;
      }
      video.muted = true;
      if (video.paused) {
        var played = video.play();
        if (played && played['catch']) played['catch'](noop);
      }
    }

    var host = slide.querySelector('[data-apex-hero-embed]');
    if (host && isDisplayed(host) && !host.querySelector('iframe')) {
      var frame = document.createElement('iframe');
      frame.className = 'apex-hero__embed';
      frame.setAttribute('src', host.getAttribute('data-apex-hero-embed'));
      frame.setAttribute('title', host.getAttribute('data-title') || 'Background video');
      frame.setAttribute('allow', 'autoplay; encrypted-media');
      frame.setAttribute('tabindex', '-1');
      frame.setAttribute('aria-hidden', 'true');
      host.appendChild(frame);
    }
  };

  HeroSlider.prototype.deactivateMedia = function (slide, leaving) {
    var stop = function () {
      var videos = toArray(slide.querySelectorAll('[data-apex-hero-video]'));
      for (var i = 0; i < videos.length; i++) {
        if (!videos[i].paused) videos[i].pause();
      }
      var frame = slide.querySelector('[data-apex-hero-embed] iframe');
      if (frame) frame.parentNode.removeChild(frame);
    };

    /* The outgoing slide keeps moving until the crossfade has covered it. */
    if (leaving) {
      this.mediaTimers.push(window.setTimeout(stop, TRANSITION_MS));
    } else {
      stop();
    }
  };

  /* Re-evaluate which media copy is visible after a breakpoint change. */
  HeroSlider.prototype.syncMedia = function () {
    for (var i = 0; i < this.slides.length; i++) {
      if (i === this.index) {
        this.activateMedia(this.slides[i]);
      } else {
        this.deactivateMedia(this.slides[i], false);
      }
    }
  };

  /* ---------------- Autoplay ---------------- */

  HeroSlider.prototype.stop = function () {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.waitingVideo) {
      var wait = this.waitingVideo;
      this.waitingVideo = null;
      wait.video.removeEventListener('playing', wait.release);
      window.clearTimeout(wait.cap);
    }
    this.pauseDot();
  };

  /* The video that is actually visible on this slide at the current breakpoint. */
  HeroSlider.prototype.activeVideo = function (slide) {
    var videos = toArray(slide.querySelectorAll('[data-apex-hero-video]'));
    for (var i = 0; i < videos.length; i++) {
      if (isDisplayed(videos[i])) return videos[i];
    }
    return null;
  };

  /* Warm up the next slide's video while the current one is showing, so it
     is ready to play the moment the slider moves on. */
  HeroSlider.prototype.preloadNext = function () {
    if (this.slides.length < 2) return;
    var next = this.slides[(this.index + 1) % this.slides.length];
    var video = this.activeVideo(next);
    if (!video || video.getAttribute('data-apex-preloaded') === 'true') return;
    video.setAttribute('data-apex-preloaded', 'true');
    video.setAttribute('preload', 'auto');
    try {
      video.load();
    } catch (error) {
      /* Some browsers throw on load() mid-playback; preload="auto" still applies. */
    }
  };

  /* Longest we wait for a slide's video before advancing anyway. */
  var VIDEO_WAIT_MS = 10000;

  HeroSlider.prototype.play = function () {
    var self = this;
    this.stop();
    if (!this.autoplay) return;
    if (this.pressed || this.editing || document.hidden) return;

    var startTimer = function () {
      if (self.timer || self.waitingVideo) return;
      self.timer = window.setTimeout(function () {
        self.timer = null;
        self.goTo(self.index + 1);
      }, self.delay);
      self.runDot();
    };

    /* If the active slide has a video that is not yet playing, hold the
       autoplay timer until it starts (capped), so the slide's delay counts
       from the moment the video is visible rather than from its poster. */
    var video = this.activeVideo(this.slides[this.index]);
    if (video && (video.paused || video.readyState < 3)) {
      var released = false;
      var release = function () {
        if (released) return;
        released = true;
        self.waitingVideo = null;
        video.removeEventListener('playing', release);
        window.clearTimeout(cap);
        startTimer();
      };
      var cap = window.setTimeout(release, VIDEO_WAIT_MS);
      this.waitingVideo = { video: video, release: release, cap: cap };
      video.addEventListener('playing', release);
      this.preloadNext();
      return;
    }

    startTimer();
    this.preloadNext();
  };

  /* ---------------- Events ---------------- */

  HeroSlider.prototype.bind = function () {
    var self = this;
    var root = this.root;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.goTo(self.index - 1);
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.goTo(self.index + 1);
      });
    }

    for (var d = 0; d < this.dots.length; d++) {
      (function (dot, index) {
        dot.addEventListener('click', function () {
          self.goTo(index);
        });
      })(this.dots[d], d);
    }

    root.addEventListener('keydown', function (event) {
      if (event.defaultPrevented) return;
      if (event.key === 'ArrowLeft' || event.key === 'Left') {
        event.preventDefault();
        self.goTo(self.index - 1);
      } else if (event.key === 'ArrowRight' || event.key === 'Right') {
        event.preventDefault();
        self.goTo(self.index + 1);
      }
    });

    /* Autoplay keeps running while the cursor rests on the hero or a control
       has focus; it only pauses during an active press/drag, while a video
       plays, in the theme editor, or when the tab is hidden. */

    var release = function () {
      if (!self.pressed) return;
      self.pressed = false;
      self.play();
    };

    if (window.PointerEvent) {
      root.addEventListener('pointerdown', function () {
        self.pressed = true;
        self.stop();
      });
      window.addEventListener('pointerup', release);
      window.addEventListener('pointercancel', release);
    } else {
      root.addEventListener(
        'touchstart',
        function () {
          self.pressed = true;
          self.stop();
        },
        { passive: true }
      );
      window.addEventListener('touchend', release, { passive: true });
      window.addEventListener('touchcancel', release, { passive: true });
    }
    this.release = release;

    /* Basic touch swipe. */
    var startX = null;
    var startY = null;

    root.addEventListener(
      'touchstart',
      function (event) {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      },
      { passive: true }
    );

    root.addEventListener(
      'touchend',
      function (event) {
        if (startX === null) return;
        var dx = event.changedTouches[0].clientX - startX;
        var dy = event.changedTouches[0].clientY - startY;
        startX = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        self.goTo(self.index + (dx < 0 ? 1 : -1));
      },
      { passive: true }
    );

    /* Background videos should not keep streaming in a hidden tab. */
    this.onVisibility = function () {
      if (document.hidden) {
        self.stop();
        for (var i = 0; i < self.slides.length; i++) self.deactivateMedia(self.slides[i], false);
      } else {
        self.syncMedia();
        self.play();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    /* Crossing the 1024px breakpoint swaps which media copy is displayed. */
    var frame = null;
    this.onResize = function () {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(function () {
        self.syncMedia();
      });
    };
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
    if (typeof window.ResizeObserver === 'function') {
      this.resizeObserver = new window.ResizeObserver(this.onResize);
      this.resizeObserver.observe(root);
    }

    var motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    if (motionQuery) {
      this.onMotion = function () {
        self.autoplay = root.getAttribute('data-autoplay') === 'true' && self.slides.length > 1 && !motionQuery.matches;
        self.play();
      };
      if (motionQuery.addEventListener) motionQuery.addEventListener('change', this.onMotion);
      else if (motionQuery.addListener) motionQuery.addListener(this.onMotion);
    }
  };

  /* Theme editor: jump to the slide whose block is selected and hold there. */
  HeroSlider.prototype.showBlock = function (target) {
    for (var i = 0; i < this.slides.length; i++) {
      if (this.slides[i] === target || this.slides[i].contains(target)) {
        this.editing = true;
        this.stop();
        if (i !== this.index) {
          var previous = this.index;
          this.index = i;
          this.render(previous);
        }
        return true;
      }
    }
    return false;
  };

  HeroSlider.prototype.releaseBlock = function () {
    this.editing = false;
    this.play();
  };

  HeroSlider.prototype.destroy = function () {
    this.stop();
    if (this.leaveTimer) window.clearTimeout(this.leaveTimer);
    for (var i = 0; i < this.mediaTimers.length; i++) window.clearTimeout(this.mediaTimers[i]);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    window.removeEventListener('pointerup', this.release);
    window.removeEventListener('pointercancel', this.release);
    window.removeEventListener('touchend', this.release);
    window.removeEventListener('touchcancel', this.release);
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  };

  /* ---------------- Boot ---------------- */

  var instances = [];

  function init(scope) {
    var roots = toArray((scope || document).querySelectorAll('[data-apex-hero]'));
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].getAttribute('data-apex-ready') === 'true') continue;
      roots[i].setAttribute('data-apex-ready', 'true');
      instances.push(new HeroSlider(roots[i]));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    for (var i = instances.length - 1; i >= 0; i--) {
      if (event.target.contains(instances[i].root)) {
        instances[i].destroy();
        instances.splice(i, 1);
      }
    }
  });

  document.addEventListener('shopify:block:select', function (event) {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].root.contains(event.target)) {
        instances[i].showBlock(event.target);
      }
    }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].root.contains(event.target)) {
        instances[i].releaseBlock();
      }
    }
  });
})();
